import { CircularProgress } from "@mui/material";
import QRCode from "qrcode";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent
} from "react";
import { useTranslation } from "react-i18next";
import {
  SetupActions,
  SetupCancelButton,
  SetupCodeTextField,
  SetupConfirmButton,
  SetupDescription,
  SetupErrorText,
  SetupFieldContainer,
  SetupFieldLabel,
  SetupFieldsContainer,
  SetupHeader,
  SetupHint,
  SetupLoadingContainer,
  SetupPanel,
  SetupQrImage,
  SetupTextField,
  SetupTitle,
  SetupWarning
} from "./ProfileMfaConfig.styles";
import { Spinner } from "../../components";
import { MFA_FACTOR } from "../../consts";
import { useMfaUserSettingsMutation, useMfaUserSettingsQuery } from "../../queries/mfaPreferences";
import {
  startTotpSetupAuthenticatedV3,
  verifyTotpSetupAuthenticatedV3
} from "../../services/authCognitoV3";
import { useAuthStore } from "../../stores";
import { isCognitoInvalidAccessTokenError } from "../../utils/mfa";

const OTP_LENGTH = 6;
const ISSUER_NAME = "OpenLearning";

export function ConfigureTotpModal({ 
  openConfigureTotpSuccessModal,
  onCancel,
  onBusyChange
}: { 
  openConfigureTotpSuccessModal: () => void;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const accessToken = useAuthStore((state) => state.session?.getAccessToken().getJwtToken() ?? "");
  const userData = useAuthStore((state) => state.userData);

  const [totpSetupSecret, setTotpSetupSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [setupAttempt, setSetupAttempt] = useState(0);
  // Il codice TOTP è monouso: dopo la verifica Cognito un nuovo tentativo ripete solo il salvataggio BE.
  const [isTotpVerified, setIsTotpVerified] = useState(false);
  const userSettings = useMfaUserSettingsQuery();
  const { mutateAsync: saveMfaUserSettings } = useMfaUserSettingsMutation();

  const accountName = userData?.email || userData?.external_id || "user";
  const isCodeValid = /^\d{6}$/.test(totpCode);

  const otpAuthUri = useMemo(() => {
    if (!totpSetupSecret) {
      return "";
    }

    return (
      `otpauth://totp/${encodeURIComponent(ISSUER_NAME)}:${encodeURIComponent(accountName)}` +
      `?secret=${encodeURIComponent(totpSetupSecret)}` +
      `&issuer=${encodeURIComponent(ISSUER_NAME)}`
    );
  }, [accountName, totpSetupSecret]);

  useEffect(() => {
    let isMounted = true;

    async function startSetup() {
      if (!accessToken) {
        setErrorMessage(t("profile_mfa_error_invalid_session"));
        setIsLoading(false);
        return;
      }

      try {
        setErrorMessage("");
        setIsLoading(true);
        const setup = await startTotpSetupAuthenticatedV3({ accessToken });

        if (isMounted) {
          setTotpSetupSecret(setup.secretCode);
        }
      } catch {
        if (isMounted) {
          setErrorMessage(t("profile_mfa_error_setup_start"));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    startSetup();

    return () => {
      isMounted = false;
    };
  }, [accessToken, setupAttempt, t]);

  useEffect(() => {
    let isMounted = true;

    if (!otpAuthUri) {
      setQrCodeDataUrl("");
      setIsQrLoading(false);
      return;
    }

    setIsQrLoading(true);
    setQrCodeDataUrl("");
    QRCode.toDataURL(otpAuthUri, { margin: 1, width: 220 })
      .then((dataUrl: string) => {
        if (isMounted) {
          setQrCodeDataUrl(dataUrl);
        }
      })
      .catch(() => {
        if (isMounted) {
          setQrCodeDataUrl("");
        }
      })
      .finally(() => {
        if (isMounted) setIsQrLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [otpAuthUri]);

  const verifySetup = async () => {
    if (isLoading || !accessToken || !totpSetupSecret || !isCodeValid) {
      return;
    }

    try {
      setErrorMessage("");
      setIsLoading(true);
      onBusyChange(true);

      if (!isTotpVerified) {
        try {
          await verifyTotpSetupAuthenticatedV3({ accessToken, code: totpCode });
          setIsTotpVerified(true);
        } catch (error: unknown) {
          const errorName = error instanceof Error ? error.name : "";

          if (errorName === "CodeMismatchException" || errorName === "ExpiredCodeException") {
            setErrorMessage(t("profile_mfa_error_code_invalid"));
          } else if (errorName === "NotAuthorizedException" && isCognitoInvalidAccessTokenError(error)) {
            setErrorMessage(t("profile_mfa_error_invalid_session"));
          } else if (errorName === "NotAuthorizedException") {
            setErrorMessage(t("profile_mfa_error_too_many_attempts"));
          } else {
            setErrorMessage(t("profile_mfa_error_code_verify"));
          }
          return;
        }
      }

      try {
        // Stato desiderato completo: TOTP attivo e preferito, email invariata.
        const settings = userSettings.data ?? (await userSettings.refetch()).data;
        if (!settings) throw new Error("mfa-user-settings non disponibile");

        await saveMfaUserSettings({
          email_enabled: settings.enabledFactors.includes(MFA_FACTOR.EMAIL_OTP),
          preferred_mfa: "TOTP",
          totp_enabled: true
        });
      } catch {
        setErrorMessage(t("profile_mfa_error_totp_enable"));
        return;
      }

      openConfigureTotpSuccessModal();
    } finally {
      onBusyChange(false);
      setIsLoading(false);
    }
  };

  if (isLoading && !totpSetupSecret) {
    return (
      <SetupLoadingContainer>
        <Spinner padding="0" size={ 64 } />
      </SetupLoadingContainer>
    );
  }

  if (!totpSetupSecret) {
    return (
      <SetupPanel>
        <SetupTitle>{ t("profile_mfa_configure_totp_title") }</SetupTitle>
        <SetupErrorText role="alert">{ errorMessage }</SetupErrorText>
        <SetupActions>
          <SetupCancelButton fullWidth onClick={ onCancel } type="button" variant="outlined">
            { t("profile_mfa_cancel") }
          </SetupCancelButton>
          <SetupConfirmButton
            disabled={ !accessToken }
            fullWidth
            onClick={ () => setSetupAttempt((attempt) => attempt + 1) }
            type="button"
            variant="contained"
          >
            { t("retry") }
          </SetupConfirmButton>
        </SetupActions>
      </SetupPanel>
    );
  }

  return (
    <SetupPanel>
      <SetupHeader>
        <SetupTitle>
          { t("profile_mfa_configure_totp_title") }
        </SetupTitle>
        <SetupDescription>
          { t("profile_mfa_configure_totp_description") }
        </SetupDescription>
      </SetupHeader>

      { qrCodeDataUrl ? (
        <SetupQrImage
          alt={ t("profile_mfa_configure_totp_qr_alt") }
          src={ qrCodeDataUrl }
        />
      ) : isQrLoading ? (
        <Spinner padding="0" size={ 48 } />
      ) : null }

      <SetupWarning
        severity="warning"
      >
        { t("profile_mfa_configure_totp_warning") }
      </SetupWarning>

      <SetupFieldsContainer>
        <SetupHint>
          { t("profile_mfa_configure_totp_manual_hint") }
        </SetupHint>

        <SetupFieldContainer>
          <SetupFieldLabel>
            { t("profile_mfa_configure_totp_setup_key") }
          </SetupFieldLabel>
          <SetupTextField
            fullWidth
            hiddenLabel
            InputProps={ { readOnly: true } }
            inputProps={ { "aria-label": t("profile_mfa_configure_totp_setup_key") } }
            placeholder={ t("profile_mfa_configure_totp_setup_key") }
            value={ totpSetupSecret }
          />
        </SetupFieldContainer>

        <SetupFieldContainer>
          <SetupFieldLabel>
            { t("profile_mfa_configure_totp_code_label") }
          </SetupFieldLabel>
          <SetupCodeTextField
            disabled={ isLoading || isTotpVerified }
            fullWidth
            hiddenLabel
            inputProps={ {
              "aria-label": t("profile_mfa_configure_totp_code_label"),
              autoComplete: "one-time-code",
              inputMode: "numeric",
              maxLength: OTP_LENGTH,
              pattern: "[0-9]{6}"
            } }
            onChange={ (event: ChangeEvent<HTMLInputElement>) => {
              const sanitized = event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
              setTotpCode(sanitized);
            } }
            placeholder={ t("profile_mfa_configure_totp_code_label") }
            value={ totpCode }
          />
        </SetupFieldContainer>
      </SetupFieldsContainer>

      { errorMessage ? (
        <SetupErrorText role="alert">
          { errorMessage }
        </SetupErrorText>
      ) : null }

      <SetupActions>
        <SetupCancelButton
          disabled={ isLoading }
          fullWidth
          type="button"
          variant="outlined"
          onClick={ onCancel }
        >
          { t("profile_mfa_cancel") }
        </SetupCancelButton>
        <SetupConfirmButton
          disabled={ isLoading || !isCodeValid }
          fullWidth
          onClick={ verifySetup }
          startIcon={ isLoading ? <CircularProgress color="inherit" size={ 16 } /> : undefined }
          type="button"
          variant="contained"
        >
          { t("confirm") }
        </SetupConfirmButton>
      </SetupActions>
    </SetupPanel>
  );
}
